import React from 'react';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { PageHeader } from '../common';
import CostDashboard from '../analytics/CostDashboard';
import ErrorDashboard from '../analytics/ErrorDashboard';
import ExecutionList from '../execution/ExecutionList';
import RunsWorkspace from '../execution/RunsWorkspace';

type AnalyticsTab = 'runs' | 'cost' | 'reliability';

const DEFAULT_TAB: AnalyticsTab = 'runs';

const normalizeTab = (value: string | null): AnalyticsTab => {
  if (value === 'runs' || value === 'cost' || value === 'reliability') {
    return value;
  }

  return DEFAULT_TAB;
};

const Analytics: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = normalizeTab(searchParams.get('tab'));
  const legacyRunsView = searchParams.get('view') === 'legacy';

  const handleTabChange = (_event: React.SyntheticEvent, nextTab: AnalyticsTab) => {
    if (nextTab === DEFAULT_TAB) {
      setSearchParams({}, { replace: true });
      return;
    }

    setSearchParams({ tab: nextTab }, { replace: true });
  };

  return (
    <Box>
      <PageHeader title="Insights" />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review run history, cost patterns, and reliability trends from one shared workspace.
      </Typography>

      <Tabs
        value={tab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3 }}
      >
        <Tab value="runs" label="Runs" />
        <Tab value="cost" label="Cost" />
        <Tab value="reliability" label="Reliability" />
      </Tabs>

      {tab === 'runs' && (legacyRunsView ? <ExecutionList embedded /> : <RunsWorkspace embedded />)}
      {tab === 'cost' && <CostDashboard />}
      {tab === 'reliability' && <ErrorDashboard />}
    </Box>
  );
};

export default Analytics;
